import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  App,
  Button,
  Card,
  Input,
  Select,
  Space,
  Spin,
  Table,
  Typography,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { FileTextOutlined, ReloadOutlined } from "../components/icons";
import { useScriptAssistantAccess } from "../hooks/useScriptAssistantAccess";
import { useScriptSheetData } from "../hooks/scriptSheet/useScriptSheetData";
import { useScriptImageUnderstanding } from "../hooks/scriptSheet/useScriptImageUnderstanding";
import { useScriptRolePipeline } from "../hooks/scriptSheet/useScriptRolePipeline";
import { ScriptFlowProgress } from "../components/scriptSheet/ScriptFlowProgress";
import { ScriptPreviewSummary } from "../components/scriptSheet/ScriptPreviewSummary";
import { ScriptSheetCellContent } from "../components/scriptSheet/ScriptSheetCellContent";
import { ScriptAssignmentReviewPanel } from "../components/scriptSheet/ScriptAssignmentReviewPanel";
import { formatScriptContentRowRange } from "../../shared/feishuScriptAssignment";
import { buildScriptAssignmentCacheLookup, isResumableScriptRoleMatchJob } from "../../shared/scriptRoleAssignmentCache";
import { trpc } from "../trpc";
import type {
  SimpleActiveItem,
  SimpleFlowPhase,
  SimpleStepState,
} from "../hooks/scriptSheet/types";

const { Text, Paragraph } = Typography;

function createInitialSteps(): SimpleStepState[] {
  return [
    { step: "images", status: "wait" },
    { step: "persona", status: "wait" },
    { step: "matching", status: "wait" },
    { step: "writeback", status: "wait" },
  ];
}

function renderMemberLabel(number?: string, nickname?: string) {
  if (number?.trim()) {
    return (
      <span>
        <Text strong>{number}</Text>
        {nickname?.trim() ? (
          <Text type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>
            {nickname}
          </Text>
        ) : null}
      </span>
    );
  }
  return <Text>{nickname?.trim() || "—"}</Text>;
}

export function ScriptSheetSimplePage() {
  const { message } = App.useApp();
  const { access: scriptAccess } = useScriptAssistantAccess();
  const sheetData = useScriptSheetData();
  const { running: imageRunning, understandAllImages } = useScriptImageUnderstanding();
  const { running: pipelineRunning, assignmentResult, setAssignmentResult, runPersonaAndMatch } =
    useScriptRolePipeline();

  const [flowPhase, setFlowPhase] = useState<SimpleFlowPhase>("idle");
  const [steps, setSteps] = useState<SimpleStepState[]>(createInitialSteps);
  const [activeItems, setActiveItems] = useState<SimpleActiveItem[]>([]);
  const [confirming, setConfirming] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [pipelineError, setPipelineError] = useState<string | null>(null);
  const [writeResult, setWriteResult] = useState<{
    reviewId: string;
    updatedCells: number;
    changedRoleCount: number;
  } | null>(null);
  const [resumeOffer, setResumeOffer] = useState(false);
  const [tablePagination, setTablePagination] = useState({ current: 1, pageSize: 20 });

  const isLocked =
    flowPhase === "processing" || flowPhase === "writing" || imageRunning || pipelineRunning;

  const updateStep = useCallback(
    (step: SimpleStepState["step"], patch: Partial<SimpleStepState>) => {
      setSteps((prev) =>
        prev.map((item) => (item.step === step ? { ...item, ...patch } : item))
      );
    },
    []
  );

  const {
    url,
    setUrl,
    loading,
    credentialsLoading,
    hasCredentials,
    hasImageUnderstandingModel,
    result,
    groups,
    groupsLoading,
    selectedGroup,
    setSelectedGroup,
    memberCount,
    membersLoading,
    handleRead,
    parsedRows,
    allParsedRows,
    atmosphereRows,
    messageCount,
    eligibleRoleCount,
    assignmentContentImages,
    imageUnderstandings,
    setImageUnderstandings,
    buildImageTextsPayload,
    overwriteMode,
  } = sheetData;

  const assignmentCacheLookup = useMemo(() => {
    if (!result || !selectedGroup) return null;
    return buildScriptAssignmentCacheLookup({
      spreadsheetToken: result.spreadsheetToken,
      sheetId: result.sheetId,
      group: selectedGroup,
      parsedRows: allParsedRows,
      overwriteMode,
    });
  }, [allParsedRows, result, selectedGroup, overwriteMode]);

  useEffect(() => {
    if (!assignmentCacheLookup || flowPhase !== "preview") return;

    let cancelled = false;
    void trpc.feishuSheets.getRoleAssignmentCache
      .query(assignmentCacheLookup)
      .then((cache) => {
        if (cancelled || !cache) return;
        const hasIncomplete =
          cache.matchJob && isResumableScriptRoleMatchJob(cache.matchJob);
        const hasPartial =
          Object.keys(cache.personas).length > 0 &&
          cache.assignments.length < cache.tempRoles.length;
        if (hasIncomplete || hasPartial) {
          setResumeOffer(true);
        }
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [assignmentCacheLookup, flowPhase]);

  useEffect(() => {
    if (!assignmentResult || !selectedGroup) return;
    if (assignmentResult.group === selectedGroup) return;
    setAssignmentResult(null);
    setReviewOpen(false);
    setWriteResult(null);
    setFlowPhase(result ? "preview" : "idle");
  }, [assignmentResult, result, selectedGroup, setAssignmentResult]);

  const previewTableData = useMemo(() => {
    return atmosphereRows.map((row) => ({
      key: String(row.rowIndex),
      rowNumber: row.rowNumber,
      role: row.role,
      content: row.cells.C ?? "",
      number: row.cells.D ?? "",
      cellImages: row.cellImages,
    }));
  }, [atmosphereRows]);

  const resultTableData = useMemo(() => {
    if (!assignmentResult) return [];
    const tempRoleMeta = Object.fromEntries(
      assignmentResult.tempRoles.map((role) => [role.tempRoleId, role])
    );
    return assignmentResult.assignments
      .filter((a) => Array.isArray(a.scoreBreakdown))
      .map((assignment) => ({
        key: assignment.tempRoleId,
        tempRoleId: assignment.tempRoleId,
        contentRowRange: formatScriptContentRowRange(
          tempRoleMeta[assignment.tempRoleId]?.contentRowNumbers ?? []
        ),
        number: assignment.number,
        nickname: assignment.nickname,
      }));
  }, [assignmentResult]);

  const previewColumns: ColumnsType<(typeof previewTableData)[number]> = [
    { title: "行号", dataIndex: "rowNumber", width: 72 },
    {
      title: "角色",
      dataIndex: "role",
      width: 140,
      render: (value: string, record) => (
        <ScriptSheetCellContent
          text={value}
          imageFileToken={record.cellImages?.B}
        />
      ),
    },
    {
      title: "需发内容",
      dataIndex: "content",
      render: (value: string, record) => {
        const imageToken = record.cellImages?.C;
        return (
          <ScriptSheetCellContent
            text={value}
            imageFileToken={imageToken}
            imageUnderstanding={
              imageToken ? imageUnderstandings[imageToken]?.content : undefined
            }
          />
        );
      },
    },
    {
      title: "当前编号",
      dataIndex: "number",
      width: 100,
      render: (value: string) => value || "—",
    },
  ];

  const resultColumns: ColumnsType<(typeof resultTableData)[number]> = [
    {
      title: "气氛员角色",
      dataIndex: "tempRoleId",
      width: 120,
      render: (value: string) => `气氛员${value}`,
    },
    { title: "话术行", dataIndex: "contentRowRange", width: 120 },
    {
      title: "匹配结果",
      key: "member",
      render: (_: unknown, record) => renderMemberLabel(record.number, record.nickname),
    },
  ];

  const onRead = async () => {
    const ok = await handleRead();
    if (ok) {
      setFlowPhase("preview");
      setPipelineError(null);
      setSteps(createInitialSteps());
      setAssignmentResult(null);
      setResumeOffer(false);
      setReviewOpen(false);
      setWriteResult(null);
    }
  };

  const runFullPipeline = useCallback(async () => {
    if (!result || !selectedGroup) return;

    setConfirming(true);
    setFlowPhase("processing");
    setPipelineError(null);
    setSteps(createInitialSteps());
    setActiveItems([]);

    try {
      let imageTextsByFileToken = { ...buildImageTextsPayload() };

      if (assignmentContentImages.length > 0) {
        updateStep("images", { status: "process", label: "准备理解图片…" });
        const imageTextsFromRun = await understandAllImages(
          {
            result,
            images: assignmentContentImages,
            imageUnderstandings,
            setImageUnderstandings,
          },
          {
            onProgress: ({ done, total, activeItems: items }) => {
              updateStep("images", {
                status: "process",
                label: `理解图片 ${done}/${total}`,
              });
              setActiveItems(items);
            },
            onStepError: (error, retryCountdown) => {
              updateStep("images", {
                status: retryCountdown ? "retry" : "error",
                error,
                retryCountdown,
              });
            },
          }
        );
        imageTextsByFileToken = { ...imageTextsByFileToken, ...imageTextsFromRun };
        updateStep("images", { status: "finish" });
      } else {
        updateStep("images", { status: "finish", label: "无需理解图片" });
      }

      updateStep("persona", { status: "process", label: "准备分析画像…" });

      const finalized = await runPersonaAndMatch(
        {
          result,
          selectedGroup,
          parsedRows,
          sourceRows: allParsedRows,
          imageTextsByFileToken,
          overwriteMode,
        },
        {
          onPersonaProgress: ({ done, total, activeItems: items }) => {
            updateStep("persona", {
              status: "process",
              label: `分析画像 ${done}/${total}`,
            });
            setActiveItems(items);
          },
          onMatchProgress: ({ done, total, activeItems: items }) => {
            updateStep("persona", { status: "finish" });
            updateStep("matching", {
              status: "process",
              label: `匹配气氛员 ${done}/${total}`,
            });
            setActiveItems(items);
          },
          onStepError: (step, error, retryCountdown) => {
            updateStep(step, {
              status: retryCountdown ? "retry" : "error",
              error,
              retryCountdown,
            });
          },
        }
      );

      updateStep("persona", { status: "finish" });
      updateStep("matching", { status: "finish" });
      setActiveItems([]);
      setAssignmentResult(finalized);
      setFlowPhase("matched");
      setReviewOpen(true);
      message.success("气氛员匹配完成");
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : "处理失败";
      setPipelineError(msg);
      message.error(msg);
      setFlowPhase("preview");
    } finally {
      setConfirming(false);
    }
  }, [
    result,
    selectedGroup,
    assignmentContentImages,
    understandAllImages,
    imageUnderstandings,
    setImageUnderstandings,
    buildImageTextsPayload,
    runPersonaAndMatch,
    allParsedRows,
    parsedRows,
    overwriteMode,
    updateStep,
    setAssignmentResult,
    message,
  ]);

  const handleConfirm = () => {
    void runFullPipeline();
  };

  const handleReviewConfirmStart = () => {
    setFlowPhase("writing");
    updateStep("writeback", { status: "process", label: "正在写回飞书文档…" });
  };

  const handleReviewConfirmed = (reviewResult: {
    reviewId: string;
    updatedCells: number;
    changedRoleCount: number;
  }) => {
    updateStep("writeback", {
      status: "finish",
      label: `已写回 ${reviewResult.updatedCells} 个编号`,
    });
    setWriteResult(reviewResult);
    setFlowPhase("completed");
    setReviewOpen(false);
    message.success(`已确认并写回飞书，共更新 ${reviewResult.updatedCells} 个编号`);
  };

  const handleReviewConfirmError = (error: Error) => {
    updateStep("writeback", { status: "error", error: error.message });
    setFlowPhase("matched");
  };

  if (credentialsLoading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", minHeight: 300, alignItems: "center" }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="fade-in">
      {!hasCredentials && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          title="尚未配置飞书开放平台凭证"
          description="请联系管理员配置飞书凭证后使用。"
        />
      )}

      {!hasImageUnderstandingModel && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          title="尚未配置图片理解模型"
          description="请联系管理员在模型管理中设置图片理解模型。"
        />
      )}

      {scriptAccess.feishuScriptEnabled && scriptAccess.atmosphereGroups.length === 0 && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          title="需先授权气氛员分组"
          description="请联系管理员在「用户与权限」中为您授权气氛员分组。"
        />
      )}

      {resumeOffer && flowPhase === "preview" ? (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          title="检测到未完成的任务"
          description="上次处理尚未完成，可继续处理或重新确认开始。"
          action={
            <Button size="small" type="primary" onClick={() => void runFullPipeline()}>
              继续处理
            </Button>
          }
        />
      ) : null}

      {pipelineError ? (
        <Alert
          type="error"
          showIcon
          closable
          style={{ marginBottom: 16 }}
          title="处理失败"
          description={pipelineError}
          action={
            <Button size="small" type="primary" onClick={() => void runFullPipeline()}>
              重试处理
            </Button>
          }
          onClose={() => setPipelineError(null)}
        />
      ) : null}

      <Card
        variant="borderless"
        className="shadow-sm"
        title={
          <Space>
            <FileTextOutlined style={{ color: "#6366f1" }} />
            <span>飞书剧本</span>
          </Space>
        }
      >
        <Paragraph type="secondary">
          选择气氛员分组，粘贴飞书表格链接读取剧本。确认无误后系统将自动完成图片理解、画像分析与气氛员匹配。
        </Paragraph>

        <Space vertical size="middle" style={{ width: "100%", marginBottom: 16 }}>
          <Select
            allowClear
            showSearch
            placeholder="1. 选择气氛员分组"
            style={{ width: "100%", maxWidth: 400 }}
            value={selectedGroup}
            loading={groupsLoading}
            disabled={!hasCredentials || groupsLoading || isLocked}
            onChange={(value) => setSelectedGroup(value)}
            options={groups.map((group) => ({ label: group, value: group }))}
            notFoundContent={
              groupsLoading ? <Spin size="small" /> : "暂无分组，请联系管理员"
            }
          />

          <Space.Compact style={{ width: "100%", maxWidth: 640 }}>
            <Input
              placeholder="2. 粘贴飞书表格链接"
              value={url}
              onChange={(event) => setUrl(event.target.value)}
              onPressEnter={() => void onRead()}
              size="large"
              allowClear
              disabled={isLocked}
            />
            <Button
              type="primary"
              size="large"
              icon={<ReloadOutlined />}
              loading={loading}
              onClick={() => void onRead()}
              disabled={!hasCredentials || !selectedGroup || isLocked}
            >
              读取
            </Button>
          </Space.Compact>
        </Space>

        {result ? (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            title="已读取工作表"
            description={
              <Text strong style={{ fontSize: 15 }}>
                {result.sheetTitle || "未命名工作表"}
              </Text>
            }
          />
        ) : null}

        {flowPhase === "processing" || flowPhase === "writing" || flowPhase === "completed" ? (
          <ScriptFlowProgress
            steps={steps}
            activeItems={activeItems}
            showWriteback={flowPhase === "writing" || flowPhase === "completed"}
          />
        ) : null}

        {flowPhase === "completed" && writeResult ? (
          <Alert
            type="success"
            showIcon
            style={{ marginBottom: 16 }}
            title="写回完成"
            description={`已成功将 ${writeResult.updatedCells} 个气氛员编号写回飞书文档。`}
          />
        ) : null}

        {result && (flowPhase === "preview" || flowPhase === "processing") ? (
          <>
            {flowPhase === "preview" ? (
              <ScriptPreviewSummary
                messageCount={messageCount}
                roleCount={eligibleRoleCount}
                memberCount={memberCount}
                membersLoading={membersLoading}
                onConfirm={handleConfirm}
                confirming={confirming}
                disabled={
                  (assignmentContentImages.length > 0 && !hasImageUnderstandingModel) ||
                  eligibleRoleCount === 0
                }
              />
            ) : null}

            <Table
              size="small"
              bordered
              columns={previewColumns}
              dataSource={previewTableData}
              pagination={{
                current: tablePagination.current,
                pageSize: tablePagination.pageSize,
                showSizeChanger: true,
                pageSizeOptions: [20, 50, 100],
                showTotal: (total) => `共 ${total} 行气氛员消息`,
                onChange: (page, pageSize) => {
                  setTablePagination({ current: page, pageSize });
                },
              }}
              scroll={{ x: "max-content" }}
              style={{ marginBottom: 16 }}
            />
          </>
        ) : null}

        {(flowPhase === "matched" || (flowPhase === "completed" && assignmentResult)) &&
        assignmentResult ? (
          <div>
            <Card
              size="small"
              title="匹配结果"
              style={{ marginBottom: 16 }}
            >
              <Table
                size="small"
                pagination={false}
                rowKey="key"
                columns={resultColumns}
                dataSource={resultTableData}
                scroll={{ x: "max-content" }}
              />
            </Card>

            {flowPhase === "matched" ? (
              <Button
                type="primary"
                size="large"
                block
                style={{ maxWidth: 400, height: 48, fontSize: 16 }}
                disabled={assignmentResult.assignments.length !== assignmentResult.tempRoles.length}
                onClick={() => setReviewOpen(true)}
              >
                审查并写入飞书
              </Button>
            ) : null}
          </div>
        ) : null}

        {!result && flowPhase === "idle" ? (
          <Text type="secondary">选择分组并读取飞书表格后，将展示剧本预览。</Text>
        ) : null}
      </Card>

      {assignmentResult && assignmentCacheLookup ? (
        <ScriptAssignmentReviewPanel
          open={reviewOpen}
          assignmentResult={assignmentResult}
          parsedRows={allParsedRows}
          sourceFingerprint={assignmentCacheLookup.sourceFingerprint}
          sheetTitle={result?.sheetTitle ?? ""}
          overwriteMode={assignmentResult.overwriteMode}
          imageTextsByFileToken={assignmentResult.imageTextsByFileToken}
          onClose={() => setReviewOpen(false)}
          onConfirmStart={handleReviewConfirmStart}
          onConfirmed={handleReviewConfirmed}
          onConfirmError={handleReviewConfirmError}
        />
      ) : null}
    </div>
  );
}
